import { useEffect, useMemo, useState } from 'react'
import { Search, Info, GitCompare, X, Plus, Users } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { DataTable } from '@/components/DataTable'
import { FormField } from '@/components/FormField'
import { Combobox } from '@/components/Combobox'
import { Toolbar } from '@/components/Toolbar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/EmptyState'
import { StateProvinceField } from '@/components/StateProvinceField'
import { formatPayrollMoney } from '@/features/payroll/format'
import {
  useMarketplacePlans,
  useMarketplaceCounties,
  type MarketplacePlan,
  type MarketplaceSearchParams,
  type HouseholdMember,
} from '@/features/payroll/benefitsMarketplace'

const METAL_VARIANT: Record<string, 'secondary' | 'info' | 'warning' | 'brand' | 'outline'> = {
  Catastrophic: 'outline',
  Bronze: 'secondary',
  Silver: 'info',
  Gold: 'warning',
  Platinum: 'brand',
}

const money = (cents: number | null) => (cents == null ? '-' : formatPayrollMoney(cents))

function metalBadge(metal: string | null) {
  if (!metal) return <span className="text-muted-foreground">-</span>
  return <Badge variant={METAL_VARIANT[metal] || 'secondary'}>{metal}</Badge>
}

const currentYear = new Date().getFullYear()

export function BenefitsMarketplacePage() {
  const [form, setForm] = useState<MarketplaceSearchParams>({
    state: '',
    zipcode: '',
    countyfips: null,
    people: [{ age: 30, tobacco: false }],
    income: 50000,
    market: 'Individual',
    year: currentYear,
  })
  const [submitted, setSubmitted] = useState<MarketplaceSearchParams | null>(null)
  const [compareIds, setCompareIds] = useState<string[]>([])
  const [tierFilter, setTierFilter] = useState<string>('all')
  const [cheapestPerTier, setCheapestPerTier] = useState(false)

  const countiesQuery = useMarketplaceCounties(form.zipcode, true)
  const counties = countiesQuery.data?.counties ?? []

  useEffect(() => {
    if (counties.length <= 1 && form.countyfips) setForm((f) => ({ ...f, countyfips: null }))
  }, [counties.length])

  const query = useMarketplacePlans(submitted ?? form, submitted != null)
  const plans = query.data?.plans ?? []

  const setMember = (i: number, patch: Partial<HouseholdMember>) =>
    setForm((f) => ({ ...f, people: f.people.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) }))
  const addMember = () => setForm((f) => ({ ...f, people: [...f.people, { age: 30, tobacco: false }] }))
  const removeMember = (i: number) => setForm((f) => ({ ...f, people: f.people.filter((_, idx) => idx !== i) }))

  const toggleCompare = (id: string) =>
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : prev.length >= 3 ? prev : [...prev, id],
    )
  const compared = useMemo(
    () => plans.filter((p) => compareIds.includes(p.external_plan_id)),
    [plans, compareIds],
  )

  const visiblePlans = useMemo(() => {
    let list = tierFilter === 'all' ? plans : plans.filter((p) => p.metal_level === tierFilter)
    if (cheapestPerTier) {
      const seen = new Set<string>()
      list = list.filter((p) => {
        const key = p.metal_level || '?'
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }
    return list
  }, [plans, tierFilter, cheapestPerTier])

  const tierCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const p of plans) counts[p.metal_level || '?'] = (counts[p.metal_level || '?'] || 0) + 1
    return counts
  }, [plans])

  const columns: ColumnDef<MarketplacePlan, unknown>[] = [
    {
      id: 'compare',
      header: '',
      cell: ({ row }) => (
        <Checkbox
          checked={compareIds.includes(row.original.external_plan_id)}
          onCheckedChange={() => toggleCompare(row.original.external_plan_id)}
          aria-label="Compare plan"
        />
      ),
    },
    {
      accessorKey: 'name',
      header: 'Plan',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-xs text-muted-foreground">{row.original.issuer_name}</div>
        </div>
      ),
    },
    { id: 'metal', header: 'Tier', cell: ({ row }) => metalBadge(row.original.metal_level) },
    { accessorKey: 'plan_type', header: 'Type', cell: ({ row }) => row.original.plan_type || '-' },
    { id: 'premium', header: 'Premium / mo', cell: ({ row }) => money(row.original.premium_minor_units) },
    {
      id: 'subsidized',
      header: 'Est. after subsidy',
      cell: ({ row }) => {
        const full = row.original.premium_minor_units
        const net = row.original.premium_with_credit_minor_units
        if (net == null) return '-'
        const saving = full != null && net < full
        return <span className={saving ? 'font-medium text-primary' : undefined}>{money(net)}</span>
      },
    },
    { id: 'deductible', header: 'Deductible', cell: ({ row }) => money(row.original.deductible_minor_units) },
    { id: 'moop', header: 'Max out-of-pocket', cell: ({ row }) => money(row.original.moop_minor_units) },
    {
      id: 'hsa',
      header: 'HSA',
      cell: ({ row }) => (row.original.hsa_eligible ? <Badge variant="success">Eligible</Badge> : <span className="text-muted-foreground">-</span>),
    },
  ]

  const canSearch = form.state.trim().length === 2 && form.people.length > 0
  const yearOptions = [currentYear, currentYear + 1].map((y) => ({ value: String(y), label: String(y) }))

  return (
    <Page title="Shop Plans" description="Browse and compare health plans available in a state.">
      <Card>
        <CardContent className="flex items-start gap-3 p-4">
          <Info className="mt-0.5 size-5 shrink-0 text-primary" />
          <p className="text-sm text-muted-foreground">
            These are indicative public marketplace plans and standard rates for reference only - not your
            company's negotiated group plan, and not a quote or an offer of coverage. Estimated subsidies assume
            marketplace (APTC) eligibility and your stated household income. Final plans, eligibility, and pricing
            come from your carrier. {query.data && !query.data.configured ? 'Showing sample data (no live marketplace key configured).' : ''}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium">State</label>
              <StateProvinceField
                country="US"
                value={form.state}
                onChange={(v) => setForm((f) => ({ ...f, state: v.toUpperCase() }))}
                placeholder="Select state"
              />
            </div>
            <FormField
              label="ZIP code"
              value={form.zipcode}
              onChange={(e) => {
                const value = e.currentTarget.value
                setForm((f) => ({ ...f, zipcode: value, countyfips: null }))
              }}
              placeholder="e.g. 90001"
              inputMode="numeric"
            />
            {counties.length > 1 && (
              <div>
                <label className="mb-1.5 block text-sm font-medium">County</label>
                <Combobox
                  value={form.countyfips}
                  onChange={(v) => setForm((f) => ({ ...f, countyfips: v ? String(v) : null }))}
                  options={counties.map((c) => ({ value: c.fips, label: c.name }))}
                  placeholder="Select county"
                />
              </div>
            )}
            <FormField
              label="Household income (annual)"
              type="number"
              value={form.income}
              onChange={(e) => {
                const value = Number(e.currentTarget.value)
                setForm((f) => ({ ...f, income: value }))
              }}
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium">Market</label>
              <Combobox
                value={form.market}
                onChange={(v) => setForm((f) => ({ ...f, market: v ? String(v) : 'Individual' }))}
                options={[
                  { value: 'Individual', label: 'Individual' },
                  { value: 'SHOP', label: 'Small business (SHOP)' },
                ]}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Plan year</label>
              <Combobox
                value={String(form.year)}
                onChange={(v) => setForm((f) => ({ ...f, year: v ? Number(v) : currentYear }))}
                options={yearOptions}
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2">
              <Users className="size-4 text-primary" />
              <h3 className="text-sm font-semibold">Who needs coverage?</h3>
            </div>
            <div className="space-y-2">
              {form.people.map((m, i) => (
                <div key={i} className="flex flex-wrap items-end gap-3 rounded-lg border p-3">
                  <FormField
                    label={i === 0 ? 'Age (primary)' : `Age (member ${i + 1})`}
                    type="number"
                    value={m.age}
                    onChange={(e) => {
                      const value = Number(e.currentTarget.value)
                      setMember(i, { age: value })
                    }}
                    containerClassName="w-32"
                  />
                  <div className="w-40">
                    <label className="mb-1.5 block text-sm font-medium">Tobacco use</label>
                    <Combobox
                      value={m.tobacco ? 'yes' : 'no'}
                      onChange={(v) => setMember(i, { tobacco: v === 'yes' })}
                      options={[
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                      ]}
                    />
                  </div>
                  {form.people.length > 1 && (
                    <Button size="icon" variant="ghost" className="size-9" onClick={() => removeMember(i)} aria-label="Remove member">
                      <X className="size-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="secondary" className="mt-2" onClick={addMember}>
              <Plus className="mr-2 size-4" /> Add household member
            </Button>
          </div>

          <Toolbar className="mt-4">
            <Button
              onClick={() => {
                setCompareIds([])
                setSubmitted({ ...form })
              }}
              disabled={!canSearch}
            >
              <Search className="mr-2 size-4" /> Search plans
            </Button>
          </Toolbar>
        </CardContent>
      </Card>

      {compared.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <GitCompare className="size-4 text-primary" />
              <h3 className="text-sm font-semibold">Compare ({compared.length})</h3>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {compared.map((p) => (
                <div key={p.external_plan_id} className="rounded-lg border p-3">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.issuer_name}</div>
                    </div>
                    <Button size="icon" variant="ghost" className="size-7" onClick={() => toggleCompare(p.external_plan_id)} aria-label="Remove from compare">
                      <X className="size-4" />
                    </Button>
                  </div>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between"><dt className="text-muted-foreground">Tier</dt><dd>{metalBadge(p.metal_level)}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd>{p.plan_type || '-'}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Premium / mo</dt><dd>{money(p.premium_minor_units)}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Est. after subsidy</dt><dd>{money(p.premium_with_credit_minor_units)}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Deductible</dt><dd>{money(p.deductible_minor_units)}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">Max out-of-pocket</dt><dd>{money(p.moop_minor_units)}</dd></div>
                    <div className="flex justify-between"><dt className="text-muted-foreground">HSA</dt><dd>{p.hsa_eligible ? 'Eligible' : '-'}</dd></div>
                  </dl>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {submitted && query.data?.unsupported_state ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={`${query.data.state} runs its own health marketplace`}
              description={`Plan data for ${query.data.state} comes from its state-based exchange, not the federal HealthCare.gov source we use here, so it can't be shown. Try a HealthCare.gov state (e.g. TX, FL, OH, PA), or this state will be covered once direct carrier integration is connected.`}
            />
          </CardContent>
        </Card>
      ) : submitted ? (
        <div className="space-y-3">
          {plans.length > 0 && (
            <Toolbar leading={<span className="text-sm text-muted-foreground">{visiblePlans.length} of {plans.length} plans</span>}>
              <div className="w-44">
                <Combobox
                  value={tierFilter}
                  onChange={(v) => setTierFilter(v ? String(v) : 'all')}
                  options={[
                    { value: 'all', label: 'All tiers' },
                    ...['Bronze', 'Silver', 'Gold', 'Platinum', 'Catastrophic']
                      .filter((t) => tierCounts[t])
                      .map((t) => ({ value: t, label: `${t} (${tierCounts[t]})` })),
                  ]}
                />
              </div>
              <Button variant={cheapestPerTier ? 'primary' : 'secondary'} onClick={() => setCheapestPerTier((v) => !v)}>
                Cheapest per tier
              </Button>
            </Toolbar>
          )}
          <DataTable
            columns={columns}
            rows={visiblePlans}
            loading={query.isFetching}
            empty={<EmptyState title="No plans found" description="Try a different state, ZIP code, market, or plan year." />}
          />
        </div>
      ) : null}
    </Page>
  )
}
